import React, { useEffect, useState } from "react";
import "./Home.css";
import { SEO } from "../../components/SEO";
import { useForm } from "react-hook-form";
import axios from "axios";
import { toast } from "react-toastify";
import { Loader } from "../../components/Loader/Loader";
import { formatDate } from "../../utils/formatDate";

export const Home = () => {
  const [loading, setLoading] = useState(false);
  const [isImageLoading, setIsImageLoading] = useState(true);
  const [dialog, setDialog] = useState(false);
  const [barkData, setBarkData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [magnify, setMagnify] = useState(false);
  const [magnifyPosition, setMagnifyPosition] = useState("0% 0%");
  const [zoomImg, setZoomImg] = useState("");
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm();

  const handleMouseMove = (e) => {
    const { left, top, width, height } =
      e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - left) / width) * 100;
    const y = ((e.clientY - top) / height) * 100;
    setMagnifyPosition(`${x}% ${y}%`);
    setMagnify(true);
    setZoomImg(e.currentTarget.dataset.name);
  };

  // Fetch data from the backend for a specific page
  const fetchBarkData = async (page) => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/species/get-details?page=${page}`
      );
      const result = response.data;
      if (result.success) {
        setBarkData(result.details.speciesList[0]);
        setCurrentPage(result.details.currentPage);
        setTotalPages(result.details.totalPages);
        fetchValidateData(result.details.speciesList[0]._id);
      }
    } catch (error) {
      console.error("Error fetching bark data:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchValidateData = async (speciesId) => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/species/check-valid-count`,
        {
          speciesId: speciesId,
          phoneNumber: localStorage.getItem("phoneNumber"),
        }
      );
      const result = response.data;
      if (result.success) {
        const { userSubmitted } = result.details.validatedData;
        setIsSubmitted(userSubmitted);
      }
    } catch (error) {
      console.error("Error fetching bark data:", error);
    }
  };

  const handleNext = () => {
    if (currentPage < totalPages) {
      setIsImageLoading(true);
      fetchBarkData(currentPage + 1);
    }
  };

  const handlePrevious = () => {
    if (currentPage > 1) {
      setIsImageLoading(true);
      fetchBarkData(currentPage - 1);
    }
  };

  const handleValidate = async () => {
    try {
      setLoading(true);
      await axios.post(`${process.env.REACT_APP_BASE_URL}/species/validate`, {
        speciesId: barkData._id,
        phoneNumber: localStorage.getItem("phoneNumber"),
      });
      fetchBarkData(currentPage);
      toast.success("Validated Successfully!");
    } catch (error) {
      console.error("Error validating:", error);
      toast.error("Unable to validate, please try again after sometime!");
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async (data) => {
    try {
      setLoading(true);
      await axios.post(`${process.env.REACT_APP_BASE_URL}/species/invalidate`, {
        speciesId: barkData._id,
        speciesName: data.correctName,
        phoneNumber: localStorage.getItem("phoneNumber"),
      });
      fetchBarkData(currentPage);
      setDialog(false);
      toast.success("Name Submitted Successfully!");
    } catch (error) {
      toast.error("Unable to submit, please try again after sometime!");
      console.error("Error invalidating:", error);
    } finally {
      handleDialogClose();
      setLoading(false);
    }
  };

  const handleDialogClose = () => {
    reset();
    setDialog(false);
  };

  useEffect(() => {
    fetchBarkData(currentPage);
  }, [currentPage]);

  return (
    <>
      {loading && <Loader />}
      <div className="home-main-section">
        <SEO
          title="Bark Validation | Home"
          description="Welcome to the Home page of my app."
        />
        {barkData && (
          <div className="home-main-container">
            {/* Image Section */}
            <div className="home-image-section">
              <div className="arrows arr-left" onClick={handlePrevious} />
              <div className="img-container">
                {isImageLoading && <div className="image-skeleton" />}
                <div
                  className="img-div"
                  style={{ display: isImageLoading ? "none" : "block" }}
                  onMouseMove={handleMouseMove}
                  onMouseLeave={() => setMagnify(false)}
                  data-name="bark_image"
                >
                  <img
                    src={`https://iofe-greenbase-bucket.s3.us-east-1.amazonaws.com/nCount/validation-data/${barkData?.bark_image}`}
                    alt="Bark-Image"
                    onError={(e) => {
                      e.target.onerror = null; // Prevent infinite loop if fallback fails
                      e.target.src = "/no-image-icon.png"; // Set fallback image
                    }}
                    onLoad={() => setIsImageLoading(false)}
                  />
                </div>
                <div
                  className="img-div"
                  style={{ display: isImageLoading ? "none" : "block" }}
                  onMouseMove={handleMouseMove}
                  onMouseLeave={() => setMagnify(false)}
                  data-name="tree_image"
                >
                  <img
                    src={`https://iofe-greenbase-bucket.s3.us-east-1.amazonaws.com/nCount/validation-data/${barkData?.tree_image}`}
                    alt="Tree-Image"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = "/no-image-icon.png"; // Set fallback image
                    }}
                    onLoad={() => setIsImageLoading(false)}
                  />
                </div>
              </div>
              <div className="arrows arr-right" onClick={handleNext} />
            </div>

            {/* Mobile View Arrows */}
            <div className="mob-arr-container">
              <div
                className="mob-arrows mob-arr-left"
                onClick={handlePrevious}
              />
              <div className="mob-arrows mob-arr-right" onClick={handleNext} />
            </div>

            {/* Info Section */}
            <div className="home-info-section">
              <div className="info-container">
                <div className="input-div">
                  <strong>Scientific Name:</strong>
                  <p>{barkData.scientific_species_name}</p>
                </div>
                <div className="input-div">
                  <strong>Species Location:</strong>
                  <p>
                    {barkData.latitude}, {barkData.longitude},{" "}
                    {barkData.altitude}
                  </p>
                </div>
                <div className="input-div">
                  <strong>Local Name:</strong>
                  <p>{barkData.local_species_name}</p>
                </div>
                <div className="input-div">
                  <strong>Submitted On:</strong>
                  <p>{formatDate(barkData.submission_date)}</p>
                </div>
                {isSubmitted ? (
                  <button disabled className="dis-btn">
                    Validated
                  </button>
                ) : (
                  <>
                    <button
                      className="valid-btn"
                      disabled={isSubmitted}
                      onClick={handleValidate}
                    >
                      Validate
                    </button>

                    <button
                      className="invalid-btn"
                      disabled={isSubmitted}
                      onClick={() => setDialog(true)}
                    >
                      Invalidate
                    </button>
                  </>
                )}
              </div>
            </div>

            {/* Invalidate Dialog */}
            {dialog && (
              <div className="modal-overlay" onClick={handleDialogClose}>
                <dialog
                  open
                  className="dialog-modal"
                  onClick={(e) => e.stopPropagation()}
                >
                  <form onSubmit={handleSubmit(onSubmit)}>
                    <h4>Provide Correct Name</h4>
                    <input
                      type="text"
                      placeholder="Enter Correct Name"
                      id="correctName"
                      name="correctName"
                      onWheel={(e) => e.target.blur()}
                      {...register("correctName", { required: true })}
                      className={`input-field ${
                        errors.correctName ? "error-para" : ""
                      }`}
                    />
                    <div className="modal-buttons" type="submit">
                      <button className="valid-btn" type="submit">
                        {isSubmitting ? "Submitting..." : "Submit"}
                      </button>
                      <button
                        className="invalid-btn"
                        onClick={handleDialogClose}
                      >
                        Close
                      </button>
                    </div>
                  </form>
                </dialog>
              </div>
            )}

            {/* Magnifying Container */}
            <div
              className={`${
                zoomImg === "bark_image"
                  ? "magnified-view-left"
                  : "magnified-view-right"
              } ${magnify ? "visible" : ""}`}
              style={{
                backgroundImage: `url(${encodeURI(
                  `https://iofe-greenbase-bucket.s3.us-east-1.amazonaws.com/nCount/validation-data/${barkData[zoomImg]}`
                )})`,
                backgroundPosition: magnifyPosition,
                backgroundSize: "150%",
              }}
            />
          </div>
        )}
      </div>
    </>
  );
};
